import React, { useState, Component } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import PageHeader from '../components/PageHeader';
import MetricCard from '../components/MetricCard';
import { Building2, Coins, TrendingDown, Download, Filter } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
// Mock data
const claimsByPartner = [{
  name: 'La Saponaria',
  claims: 234,
  color: '#1a4d2e'
}, {
  name: 'EcoStore',
  claims: 187,
  color: '#2d7a4f'
}, {
  name: 'GreenLife',
  claims: 156,
  color: '#4aa770'
}, {
  name: 'BioMarket',
  claims: 142,
  color: '#7bc99c'
}, {
  name: 'NaturaSì',
  claims: 98,
  color: '#a8ddb5'
}];
const topClaims = [{
  id: 1,
  reward: 'Siero Acido Ialuronico',
  partner: 'La Saponaria',
  claims: 234,
  points: 11700
}, {
  id: 2,
  reward: 'Kit Cosmetici Solidi',
  partner: 'La Saponaria',
  claims: 187,
  points: 9350
}, {
  id: 3,
  reward: 'Buono Spesa €20',
  partner: 'EcoStore',
  claims: 156,
  points: 6240
}, {
  id: 4,
  reward: 'Workshop Sostenibilità',
  partner: 'GreenLife',
  claims: 142,
  points: 14200
}, {
  id: 5,
  reward: 'Box Prodotti Bio',
  partner: 'BioMarket',
  claims: 98,
  points: 6860
}];
// Custom Tooltip Component
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg" data-id="element-2658">
        <p className="font-semibold text-gray-900 mb-1" data-id="element-2659">
          {label || payload[0].name}
        </p>
        <p className="text-sm text-gray-600" data-id="element-2660">
          Riscatti:{' '}
          <span className="font-medium text-gray-900" data-id="element-2661">{payload[0].value}</span>
        </p>
      </div>;
  }
  return null;
};
const MarketplaceOverview = () => {
  const [partnerFilter, setPartnerFilter] = useState('Tutti');
  const filteredClaims = partnerFilter === 'Tutti' ? topClaims : topClaims.filter(claim => claim.partner === partnerFilter);
  return <div className="flex h-screen bg-gray-50" data-id="element-2662">
      <div className="w-20 flex-shrink-0" data-id="element-2663">
        <Sidebar data-id="element-2664" />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-2665">
        <Topbar data-id="element-2666" />
        <main className="flex-1 overflow-y-auto p-4 md:p-6" data-id="element-2667">
          <PageHeader title="Panoramica Marketplace" subtitle="Analisi riscatti, premi e performance dei partner" secondaryAction={{
          label: 'Esporta Report',
          onClick: () => console.log('Export'),
          icon: <Download className="h-4 w-4 mr-2" data-id="element-2669" />
        }} data-id="element-2668" />

          {/* KPI Cards */}
          <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-2670">
            <MetricCard title="Partners Attivi" value="28" change="8.2%" isPositive={true} icon={Building2} data-id="element-2671" />
            <MetricCard title="Punti Totali Riscattati" value="892,340" change="18.7%" isPositive={true} icon={Coins} data-id="element-2672" />
            <MetricCard title="Riscatti Totali" value="817" change="15.3%" isPositive={true} icon={TrendingDown} data-id="element-2673" />
          </div>

          {/* Charts Row */}
          <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2" data-id="element-2674">
            {/* Claims by Partner */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm" data-id="element-2675">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-2676">
                Distribuzione Riscatti per Partner
              </h3>
              <div className="h-80" data-id="element-2677">
                <ResponsiveContainer width="100%" height="100%" data-id="element-2678">
                  <PieChart data-id="element-2679">
                    <Pie data={claimsByPartner} cx="50%" cy="50%" labelLine={false} label={({
                    name,
                    percent
                  }) => `${name} ${(percent * 100).toFixed(0)}%`} outerRadius={100} fill="#8884d8" dataKey="claims" data-id="element-2680">
                      {claimsByPartner.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-2681" />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip data-id="element-2683" />} data-id="element-2682" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Claims by Partner Bar Chart */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm" data-id="element-2684">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-2685">
                Riscatti per Partner
              </h3>
              <div className="h-80" data-id="element-2686">
                <ResponsiveContainer width="100%" height="100%" data-id="element-2687">
                  <BarChart data={claimsByPartner} data-id="element-2688">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-2689" />
                    <XAxis dataKey="name" stroke="#6B7280" fontSize={12} data-id="element-2690" />
                    <YAxis stroke="#6B7280" fontSize={12} data-id="element-2691" />
                    <Tooltip content={<CustomTooltip data-id="element-2693" />} cursor={{
                    fill: 'rgba(229, 231, 235, 0.3)'
                  }} data-id="element-2692" />
                    <Bar dataKey="claims" radius={[4, 4, 0, 0]} data-id="element-2694">
                      {claimsByPartner.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-2695" />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Top Claims Table */}
          <div className="rounded-lg border border-gray-200 bg-white overflow-hidden" data-id="element-2696">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-2697">
              <h3 className="text-lg font-semibold" data-id="element-2698">Top Riscatti</h3>
              <div className="flex items-center rounded-lg border border-gray-300 px-3 py-2" data-id="element-2699">
                <Filter className="mr-2 h-4 w-4 text-gray-500" data-id="element-2700" />
                <select className="bg-transparent text-sm focus:outline-none" value={partnerFilter} onChange={e => setPartnerFilter(e.target.value)} data-id="element-2701">
                  <option value="Tutti" data-id="element-2702">Tutti i Partners</option>
                  <option value="La Saponaria" data-id="element-2703">La Saponaria</option>
                  <option value="EcoStore" data-id="element-2704">EcoStore</option>
                  <option value="GreenLife" data-id="element-2705">GreenLife</option>
                  <option value="BioMarket" data-id="element-2706">BioMarket</option>
                  <option value="NaturaSì" data-id="element-2707">NaturaSì</option>
                </select>
              </div>
            </div>
            <div className="overflow-x-auto" data-id="element-2708">
              <table className="w-full" data-id="element-2709">
                <thead data-id="element-2710">
                  <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2711">
                    <th className="px-6 py-3" data-id="element-2712">Premio</th>
                    <th className="px-6 py-3" data-id="element-2713">Partner</th>
                    <th className="px-6 py-3" data-id="element-2714">Riscatti</th>
                    <th className="px-6 py-3" data-id="element-2715">Punti Totali</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-2716">
                  {filteredClaims.map(claim => <tr key={claim.id} className="hover:bg-gray-50" data-id="element-2717">
                      <td className="px-6 py-4" data-id="element-2718">
                        <div className="font-medium text-gray-900" data-id="element-2719">
                          {claim.reward}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-600" data-id="element-2720">
                        {claim.partner}
                      </td>
                      <td className="px-6 py-4 font-medium" data-id="element-2721">{claim.claims}</td>
                      <td className="px-6 py-4 font-medium text-green-600" data-id="element-2722">
                        {claim.points.toLocaleString()} punti
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>
    </div>;
};
export default MarketplaceOverview;